/**
 * Authentication Layout Component
 *
 * A centered card layout for authentication pages (login, signup, password recovery).
 * Uses Chakra UI components with the Xacto modern dark theme.
 *
 * @fileoverview Layout wrapper for authentication pages
 *
 * @example
 * <AuthLayout title="Welcome Back" subtitle="Sign in to continue">
 *   <LoginForm />
 * </AuthLayout>
 */

import React, { ReactNode } from 'react';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Card,
  CardBody,
  Image,
  useColorModeValue,
} from '@chakra-ui/react';

/**
 * Props for AuthLayout component
 */
interface AuthLayoutProps {
  /** Main title displayed at top of card */
  title: string;
  /** Optional subtitle/description below title */
  subtitle?: string;
  /** Child components to render inside the card */
  children: ReactNode;
}

/**
 * Authentication Layout Component
 *
 * Provides a consistent, centered layout for all authentication pages.
 * Uses the Xacto modern dark theme colors from the theme configuration.
 *
 * Features:
 * - Centered card with branding
 * - Modern dark theme with cyan/purple accents
 * - Responsive design (mobile-first)
 * - Customizable title and subtitle
 *
 * @param props - Component props
 * @returns Styled layout with children
 */
export const AuthLayout: React.FC<AuthLayoutProps> = ({
  title,
  subtitle,
  children,
}) => {
  const logoSrc = useColorModeValue('/logo-light.png', '/logo-darj.png');

  return (
    <Box
      minH="100vh"
      bg="bg.primary"
      display="flex"
      alignItems="center"
      justifyContent="center"
      py={8}
      px={4}
    >
      <Container maxW="md" centerContent>
        <VStack spacing={8} w="full">
          {/* Logo/Branding Section */}
          <VStack spacing={2}>
            <Image
              src={logoSrc}
              alt="Xacto Logo"
              boxSize={32}
              objectFit="contain"
              mb={2}
            />
            <Text
              fontSize="sm"
              color="text.secondary"
              textAlign="center"
            >
              Financial Services Integration Platform
            </Text>
          </VStack>

          {/* Main Card - Uses theme's Card component with glow variant */}
          <Card
            w="full"
            variant="glow"
          >
            <CardBody p={{ base: 6, md: 8 }}>
              <VStack spacing={6} align="stretch">
                {/* Card Header */}
                <VStack spacing={1} align="start">
                  <Heading
                    as="h2"
                    size="lg"
                    color="text.primary"
                    fontWeight="semibold"
                  >
                    {title}
                  </Heading>
                  {subtitle && (
                    <Text color="text.secondary" fontSize="sm">
                      {subtitle}
                    </Text>
                  )}
                </VStack>

                {/* Form Content */}
                {children}
              </VStack>
            </CardBody>
          </Card>

          {/* Footer */}
          <Text fontSize="xs" color="text.muted" textAlign="center">
            &copy; {new Date().getFullYear()} Xacto Consulting LLC. All rights reserved.
          </Text>
        </VStack>
      </Container>
    </Box>
  );
};

export default AuthLayout;
